import React, { useMemo, useState, useEffect } from "react";
import ResignationReasonInput from "../common/ResignationReasonInput";
import { useDispatch, useSelector } from "react-redux";
import "./resignationflow.css";
import { DatePicker, Input, Card, Form, Select, Row, Col, Modal, Alert, Spin, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  UploadOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  PaperClipOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { Button, message, Upload } from "antd";
import moment from "moment";
import Service from "../../service";
import { hideAuthLoader, showAuthLoader } from "../../appRedux/actions";
import { Link } from "react-router-dom/cjs/react-router-dom";
import { getResource } from "../../util/getResourcePermission";
import { isWeekoffDay, resolveWeekConfigForDate } from "../../util/weekConfig";
import config from "../../permission/config.json";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import useCompanyDateFormat from "../../hooks/useCompanyDateFormat";
import GuidedTour, { hasTourFlag } from "../common/GuidedTour/GuidedTour";

const TOUR_STORAGE_KEY = "tour_seen_resignation";
const TOUR_HINT_STORAGE_KEY = "tour_hint_dismissed_resignation";

function ResignationFlow({ isVisible, onClose, onSuccess }) {
  const dateFormat = useCompanyDateFormat();
  const dispatch = useDispatch();
  const history = useHistory();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  // "disabled" | "optional" | "required" - set by the company admin in General Settings.
  const [exitFormRequirement, setExitFormRequirement] = useState("disabled");
  const [exitFormDownloading, setExitFormDownloading] = useState(false);
  const [selDate, setSelDate] = useState(null);
  const [noticePeriodDays, setNoticePeriodDays] = useState(null);
  const [noticeLoading, setNoticeLoading] = useState(false);
  const [noticeError, setNoticeError] = useState(null);
  // The date the policy lands on, before it is walked back to a working day.
  const [policyDate, setPolicyDate] = useState(null);
  const [workweekTimeline, setWorkweekTimeline] = useState([]);
  const [lastDate, setLastDate] = useState(null);
  const companySlug = localStorage.getItem("companyDomain");
  const { authUser } = useSelector(({ auth }) => auth);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isTourHintOpen, setIsTourHintOpen] = useState(false);

  // First-time visitors get a "click here" nudge pointing at the guide
  // trigger. It stays up until dismissed, which marks it done via its own
  // storage key so it never comes back.
  useEffect(() => {
    if (hasTourFlag(TOUR_STORAGE_KEY) || hasTourFlag(TOUR_HINT_STORAGE_KEY)) return;
    const showTimer = setTimeout(() => setIsTourHintOpen(true), 600);
    return () => clearTimeout(showTimer);
  }, []);

  const OnChangeForRequest = (date, dateString) => {
    setSelDate(dateString);
  };

  useEffect(() => {
    fetchWorkweekTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The notice period is per-employee now, so the server resolves it (policy or
  // company default) and returns both candidate dates. No client-side guess: a
  // wrong number here is a wrong last working day presented to the employee as
  // fact, which is why a failure blocks submission instead of falling back.
  useEffect(() => {
    const resolveNoticePeriod = async () => {
      if (!authUser?._id) return;
      setNoticeLoading(true);
      setNoticeError(null);
      try {
        const response = await Service.makeAPICall({
          methodName: Service.postMethod,
          api_url: Service.noticePeriodResolve,
          body: { employee_id: authUser._id },
        });
        if (response?.data?.status === 200 && response?.data?.data) {
          const resolved = response.data.data;
          setNoticePeriodDays(resolved.days);
          setPolicyDate(moment(resolved.last_date_notice?.raw).format(dateFormat) || null);
          setLastDate(moment(resolved.last_date_notice?.adjusted).format(dateFormat) || null);
        } else {
          setNoticeError(
            response?.data?.message || "Could not determine your notice period."
          );
        }
      } catch (err) {
        setNoticeError("Could not determine your notice period.");
      } finally {
        setNoticeLoading(false);
      }
    };
    resolveNoticePeriod();
  }, [authUser?._id]);

  const isModalMode = typeof isVisible !== "undefined";

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setLastDate(null);
    if (onClose) {
      onClose();
    }
  };

  // The work week timeline drives which dates the "requested last date" picker disables.
  const fetchWorkweekTimeline = async () => {
    try {
      if (!authUser?._id) return;
      const empResponse = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.empById + "/" + authUser._id,
      });
      setWorkweekTimeline(empResponse?.data?.data?.workweekTimeline || []);
    } catch (error) {
      console.error("Error fetching work week timeline:", error);
    }
  };

  useEffect(() => {
    const fetchExitFormSetting = async () => {
      try {
        const response = await Service.makeAPICall({
          methodName: Service.getMethod,
          api_url: Service.getAttendanceSettings,
        });
        const requirement =
          response?.data?.data?.separation_setting?.exitFormRequirement || "disabled";
        setExitFormRequirement(requirement);
      } catch (error) {
        // Falling back to "disabled" keeps the employee able to resign if the
        // setting cannot be read; the server still enforces the real rule.
        setExitFormRequirement("disabled");
      }
    };
    fetchExitFormSetting();
  }, []);

  /** Downloads the blank clearance / exit form as a .docx for the employee to fill in. */
  const handleDownloadExitForm = async () => {
    const key = "exit-form";
    setExitFormDownloading(true);
    message.loading({ content: "Preparing the form…", key });
    try {
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.exitForm,
      });

      if (response?.data?.status !== 200 || !response?.data?.data?.content) {
        message.error({
          content: response?.data?.message || "Could not prepare the clearance / exit form",
          key,
        });
        return;
      }

      const { fileName, content } = response.data.data;
      const binary = atob(content);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName || "Clearance_Exit_Form.docx";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 10000);
      message.success({ content: "Form downloaded. Fill it in and upload it as a PDF.", key });
    } catch (error) {
      message.error({
        content: error?.message || "Could not prepare the clearance / exit form",
        key,
      });
    } finally {
      setExitFormDownloading(false);
    }
  };

  /**
   * Keeps only the latest file, and only if it is a PDF - the picker's accept
   * filter can be overridden from the file dialog.
   */
  const getExitFormFiles = (event) => {
    const files = Array.isArray(event) ? event : event?.fileList || [];
    return files.slice(-1).filter((file) => {
      const isPdf =
        file.type === "application/pdf" ||
        (file.name || "").toLowerCase().endsWith(".pdf");
      if (!isPdf) {
        message.error("The completed clearance / exit form must be a PDF");
      }
      return isPdf;
    });
  };

  const handleSubmit = async (values) => {
    // Never submit against an unknown notice period.
    if (noticeError || noticePeriodDays == null) {
      message.error(
        "Your notice period could not be determined. Please refresh and try again."
      );
      return;
    }
    try {
      const formData = new FormData();
      if (selDate) {
        formData.append(
          "request_last_date",
          moment(values?.request_last_date).format("YYYY-MM-DD")
        );
      }
      formData.append("reason", values?.reason);
      formData.append("comments", values?.comments);

      if (fileList.length > 0) {
        fileList.forEach((file) => {
          formData.append("attachment", file.originFileObj);
        });
      }

      const exitFormFile = values?.exit_form?.[0]?.originFileObj;
      if (exitFormRequirement !== "disabled" && exitFormFile) {
        formData.append("exit_form", exitFormFile);
      }

      dispatch(showAuthLoader());
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.addresign,
        body: formData,
      });
      dispatch(hideAuthLoader());
      if (response.data && response.data.data && response.data.status === 200) {
        message.success(response.data.message);
        form.resetFields();
        setFileList([]);
        setLastDate(null);
        if (isModalMode) {
          if (onClose) {
            onClose();
          }
          if (onSuccess) {
            onSuccess();
          }
        } else {
          history.push(`/${companySlug}/resignation-userlist`);
        }
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleFileChange = (info) => {
    let fileList = [...info.fileList];
    setFileList(fileList);
  };

  const disabledDateRequested = (current) => {
    if (!current) {
      return false;
    }
    const today = moment().startOf("day");
    const isBeforeToday = current < today;
    // The employee's own weekoff days, not a hardcoded Sat/Sun.
    const isWeekend = isWeekoffDay(current, resolveWeekConfigForDate(null, workweekTimeline, current));
    return isBeforeToday || isWeekend;
  };

  // Submitting against an unknown notice period would store a wrong last
  // working day, so the form stays closed until the resolve succeeds.
  const noticeBlocked = noticeLoading || Boolean(noticeError) || noticePeriodDays == null;

  const resignationForm = (
    <Form
      onFinish={handleSubmit}
      form={form}
      layout="vertical"
      className="modal-form-col"
    >
      <Row gutter={[16, 0]}>
        <Col xs={24} lg={12} xxl={8}>
          <Form.Item
            label="Date of Resignation"
            name="date_of_resignation"
            className="label-name"
            rules={[
              {
                required: true,
                message: "Please select date!",
              },
            ]}
            initialValue={moment().startOf("day")}
          >
            <p>{moment().startOf("day").format(dateFormat)}</p>
          </Form.Item>
        </Col>
        <Col xs={24} lg={12} xxl={8} data-tour="rf-notice">
          <Form.Item
            label={
              noticePeriodDays != null
                ? `Last Date As Per Notice Period (${noticePeriodDays} Days)`
                : "Last Date As Per Notice Period"
            }
            name="last_date_as_per_notice_period"
            className="label-name"
          >
            {noticeLoading && <Spin size="small" />}
            {!noticeLoading && noticeError && (
              <Alert type="error" showIcon message={noticeError} />
            )}
            {!noticeLoading && !noticeError && lastDate && (
              <p style={{ marginBottom: 0 }}>
                {lastDate}
                {/* The policy date and the last WORKING date differ whenever the
                    policy lands on a weekoff or holiday. Say so rather than
                    silently showing one of them. */}
                {policyDate && policyDate !== lastDate && (
                  <Tooltip
                    title={`Your notice period ends on ${policyDate}, which is not a working day for your shift. The last working day on or before it is ${lastDate}.`}
                  >
                    <InfoCircleOutlined style={{ marginLeft: 6, color: "#999" }} />
                  </Tooltip>
                )}
              </p>
            )}
          </Form.Item>
        </Col>
        <Col xs={24} lg={12} xxl={8}>
          <Form.Item
            label="Any Attachments"
            name="attachments"
            className="label-name"
          >
            <Upload
              multiple={false}
              beforeUpload={() => false}
              onChange={handleFileChange}
            >
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
          </Form.Item>
        </Col>
        <Col xs={24} lg={12} xxl={8} data-tour="rf-requested-date">
          <Form.Item
            label="Requested Last Date"
            name="request_last_date"
            className="label-name"
            rules={[
              {
                required: false,
              },
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }
                  const selectedDate = moment(value).startOf("day");
                  const today = moment().startOf("day");
                  if (selectedDate.isBefore(today)) {
                    return Promise.reject(
                      new Error("Requested Last Date cannot be earlier than today")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <DatePicker
              format="DD-MM-YYYY"
              disabledDate={disabledDateRequested}
              onChange={OnChangeForRequest}
            />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12} xxl={8} data-tour="rf-reason">
          <Form.Item
            label="Reason For Resignation"
            name="reason"
            className="label-name reason-resignation-from"
            rules={[
              {
                required: true,
                message: "Please select reason!",
              },
            ]}
          >
            <ResignationReasonInput  />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12} xxl={8} data-tour="rf-comments">
          <Form.Item
            label="Comments"
            name="comments"
            className="label-name reason-resignation-from"
            rules={[
              {
                required: true,
                message: "Please enter comments!",
              },
            ]}
          >
            <Input.TextArea rows={1} />
          </Form.Item>
        </Col>
        {exitFormRequirement !== "disabled" && (
          // Full width and last: the download + upload pair is taller than a
          // plain input, and inside the three-column grid it dragged the rows
          // around it out of line.
          <Col xs={24} data-tour="rf-exit-form">
            <Form.Item
              label="Clearance / Exit Form"
              className="label-name"
              required={exitFormRequirement === "required"}
              style={{ marginBottom: 8 }}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadExitForm}
                  loading={exitFormDownloading}
                  style={{ flex: "0 0 auto" }}
                >
                  Download Form (.docx)
                </Button>

                {/* Nested so the form owns the value: the error then clears by
                    itself the moment a file is attached. The built-in file list
                    is switched off - the modal's global upload styling renders
                    it as a full-width 20px-padded block - and a compact chip is
                    drawn below instead. */}
                <Form.Item
                  name="exit_form"
                  valuePropName="fileList"
                  getValueFromEvent={getExitFormFiles}
                  rules={
                    exitFormRequirement === "required"
                      ? [
                          {
                            required: true,
                            message: "Please upload the completed clearance / exit form",
                          },
                        ]
                      : []
                  }
                  style={{ marginBottom: 0, flex: "0 0 auto" }}
                >
                  <Upload
                    maxCount={1}
                    accept="application/pdf"
                    beforeUpload={() => false}
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />}>Upload Filled PDF</Button>
                  </Upload>
                </Form.Item>
              </div>

              <Form.Item
                noStyle
                shouldUpdate={(prev, next) => prev.exit_form !== next.exit_form}
              >
                {({ getFieldValue }) => {
                  const file = getFieldValue("exit_form")?.[0];
                  if (!file) return null;
                  return (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        maxWidth: "100%",
                        marginTop: 10,
                        padding: "4px 10px",
                        border: "1px solid #d9d9d9",
                        borderRadius: 6,
                        background: "#fafafa",
                        fontSize: 13,
                      }}
                    >
                      <PaperClipOutlined style={{ color: "#8c8c8c" }} />
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={file.name}
                      >
                        {file.name}
                      </span>
                      <CloseOutlined
                        // Clear the value and any error left from a previous
                        // submit; the required rule runs again on the next one.
                        onClick={() =>
                          form.setFields([{ name: "exit_form", value: [], errors: [] }])
                        }
                        style={{ color: "#8c8c8c", cursor: "pointer", fontSize: 11 }}
                      />
                    </div>
                  );
                }}
              </Form.Item>

              <div style={{ color: "#8c8c8c", fontSize: 12, marginTop: 8 }}>
                {exitFormRequirement === "required"
                  ? "Download the form, fill it in, and upload the completed copy as a PDF."
                  : "Optional. Download the form, fill it in, and upload it as a PDF."}
              </div>
            </Form.Item>
          </Col>
        )}
      </Row>
      {!isModalMode && getResource(config.RESOURCES.SEPARATION, ["create"]) && (
        <Button type="primary" htmlType="submit" disabled={noticeBlocked} data-tour="rf-save-btn">
          Save
        </Button>
      )}
    </Form>
  );

  const tourSteps = useMemo(() => {
    const steps = [
      {
        target: '[data-tour="rf-notice"]',
        title: "Your notice period",
        content:
          "Calculated automatically from your company's notice period policy - you can't edit it. If you see an (i) icon, hover it: it means the policy date landed on a weekend/holiday and got moved to the nearest working day.",
      },
      {
        target: '[data-tour="rf-requested-date"]',
        title: "Want to leave earlier?",
        content:
          "Optional. If you'd like an earlier last working day than the notice period above, request it here - it needs your manager's approval, it isn't automatic.",
      },
      {
        target: '[data-tour="rf-reason"]',
        title: "Say why",
        content: "Required. Pick whichever option is closest to your actual reason.",
      },
      {
        target: '[data-tour="rf-comments"]',
        title: "Add context",
        content: "Required - a short note for your manager and HR.",
      },
    ];
    if (exitFormRequirement !== "disabled") {
      steps.push({
        target: '[data-tour="rf-exit-form"]',
        title: "Clearance / Exit Form",
        content: `Download the blank form, fill it in outside the app, then upload the completed copy as a PDF. This is ${exitFormRequirement === "required" ? "required" : "optional"} at your company.`,
      });
    }
    steps.push({
      target: '[data-tour="rf-save-btn"]',
      title: "Submit",
      content:
        "This notifies your Reporting Manager and HR - there's no undo from here. Save stays disabled until your notice period above is confirmed.",
    });
    return steps;
  }, [exitFormRequirement]);

  const hintSteps = useMemo(
    () => [
      {
        target: '[data-tour="rf-guide-icon"]',
        shape: "circle",
        title: "New here?",
        content: "Click this icon anytime for a quick guide to this form.",
      },
    ],
    []
  );

  if (isModalMode) {
    return (
      <Modal
        title="Apply For Resignation"
        open={isVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" type="default" onClick={handleCancel}>
            Cancel
          </Button>,
          getResource(config.RESOURCES.SEPARATION, ["create"]) ? (
            <Button
              key="submit"
              type="primary"
              disabled={noticeBlocked}
              onClick={() => form.submit()}
            >
              Save
            </Button>
          ) : null,
        ]}
        width={850}
        destroyOnClose
      >
        <div className="employee-details">{resignationForm}</div>
      </Modal>
    );
  }

  return (
    <Card className="employee-card">
      <div className="inout-header text-left">
        <h2>
          Apply For Resignation{" "}
          <QuestionCircleOutlined
            data-tour="rf-guide-icon"
            className="guide-title-icon"
            role="button"
            tabIndex={0}
            onClick={() => {
              setIsTourHintOpen(false);
              setIsTourOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setIsTourHintOpen(false);
                setIsTourOpen(true);
              }
            }}
            title="Click for a step-by-step guide"
          />
        </h2>
        <div className="header-btns">
          {getResource(config.RESOURCES.SEPARATION, ["create"]) && (
            <Link to={`/${companySlug}/resignation-userlist`}>
              <Button type="primary">My Data</Button>
            </Link>
          )}
        </div>
      </div>
      <div className="employee-details">{resignationForm}</div>

      {isTourOpen && <GuidedTour steps={tourSteps} onClose={() => setIsTourOpen(false)} storageKey={TOUR_STORAGE_KEY} />}

      {!isTourOpen && isTourHintOpen && (
        <GuidedTour steps={hintSteps} onClose={() => setIsTourHintOpen(false)} storageKey={TOUR_HINT_STORAGE_KEY} />
      )}
    </Card>
  );
}

export default ResignationFlow;
